import { Link } from 'react-router';
import { cn } from '@rocco/utils/cn';

interface DirectoryEmptyResultsProps {
    /** Plural noun for the listing — "projects", "professionals", "products". */
    countNoun: string;
    /** Current pathname, the destination once the query is dropped. */
    pathname: string;
    /** Current query string, read for the active search term. */
    search: string;
    isDark: boolean;
}

/** The searched term, if this empty set came from a search rather than facets. */
const searchTermOf = (search: string): string =>
    new URLSearchParams(search).get('search')?.trim() ?? '';

/**
 * What a directory listing shows when nothing matches: the reader's own term
 * back to them where they searched, a line saying what happened, and the one
 * action that undoes it — clearing the search, or the filters that narrowed it.
 *
 * An empty set is an answer, so it is written as one. Shaped after the
 * editorial listing's empty state so the two read as one site.
 */
export const DirectoryEmptyResults = ({
    countNoun,
    pathname,
    search,
    isDark,
}: DirectoryEmptyResultsProps) => {
    const term = searchTermOf(search);

    // Clearing a search keeps the facets the reader chose; clearing facets
    // takes the whole query with it, since that is all the query holds.
    const params = new URLSearchParams(search);
    params.delete('search');
    params.delete('page');
    const query = params.toString();
    const clearTo = term && query ? `${pathname}?${query}` : pathname;

    return (
        <div
            className="flex flex-col items-start pt-16 pb-8"
            data-testid="directory-empty-results"
        >
            <p
                className={cn(
                    'm-0 font-gestura text-[2rem] leading-[1.1] tracking-[-0.025em]',
                    isDark ? 'text-offwhite' : 'text-[#141414]',
                )}
            >
                {term
                    ? `Nothing matches “${term}”`
                    : 'Nothing matches those filters'}
            </p>
            <p
                className={cn(
                    'mt-[0.75rem] mb-0 text-[1rem] leading-[1.5]',
                    isDark ? 'text-offwhite/60' : 'text-[#575757]',
                )}
            >
                {term
                    ? `Try a different word, or browse all ${countNoun} in this category.`
                    : `Try widening one of them, or start again with the whole category.`}
            </p>
            <Link
                to={clearTo}
                preventScrollReset
                className={cn(
                    'mt-[1.5rem] inline-flex h-10.5 shrink-0 items-center rounded-[var(--radius-btn-m)] px-6 text-[1rem] no-underline transition-colors',
                    isDark
                        ? 'bg-offwhite text-[#141414] hover:bg-white'
                        : 'bg-[#323232] text-white hover:bg-[#141414]',
                )}
            >
                {term ? 'Clear search' : 'Clear filters'}
            </Link>
        </div>
    );
};
